/**
 * will be @deprecated once wall framing completely moves to the new framing system
 * Note: there is another copy of this type in structural framing workspace which is
 * used for new wall framing system.
 */
import type { HeaderAssemblyId } from "building-types/framing/headerAssemblyTypes";
import type { LumberProfileDescriptor, LumberRail } from "building-types/framing/lumberTypes";
import type { OpeningHandle, RoomEdgeHandle, RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { WallFramingOpeningSettings } from "building-types/settings/wallFramingOpeningSettings";
import type { WallCondition } from "building-types/walls/wallType";
import type { NodeBSP } from "geom/bspTypes";

export enum WallFramingType {
  STANDARD_STUD = "STANDARD_STUD",
  KING_STUD = "KING_STUD",
  JACK_STUD = "JACK_STUD",
  BOTTOM_CRIPPLE_STUD = "BOTTOM_CRIPPLE_STUD",
  TOP_CRIPPLE_STUD = "TOP_CRIPPLE_STUD",
  TOP_PLATE = "TOP_PLATE",
  BOTTOM_PLATE = "BOTTOM_PLATE",
  BOTTOM_SILL_PLATE = "BOTTOM_SILL_PLATE",
  TOP_SILL_PLATE = "TOP_SILL_PLATE",
  BLOCKING = "BLOCKING",
  HEADER = "HEADER",
}

export enum OpeningSource {
  DOOR = "DOOR",
  WINDOW = "WINDOW",
}

export enum UnionType {
  OR = "OR",
  AND = "AND",
}

export type AdjacentRooms = [RoomHandle | null, RoomHandle | null]; //front is the source, second is the adjacent or null if no adjacent
export interface ResolvedOpeningFraming {
  readonly headerAssemblyId: HeaderAssemblyId | null | undefined;
  readonly headerAssemblyName: string | undefined;
  readonly numberOfJackStuds: number;
  readonly numberOfKingStuds: number;
  readonly roughOpeningBorder: number;
  readonly headerCount: number;
  readonly hasCrippleStudEnds: boolean;
  readonly hasCrippleStudsBelowOpening: boolean;
  readonly hasCrippleStudsAboveOpening: boolean;
  readonly hasTopHeaderSillPlate: boolean;
}

export interface ResolvedRoughOpeningFramingSettings extends ResolvedOpeningFraming {
  readonly openingSettings: WallFramingOpeningSettings;
  readonly gapLength: number;
  readonly headerProfile: LumberProfileDescriptor;
}

export interface WallFramingMeta {
  readonly adjacentRooms?: AdjacentRooms;
  readonly tag?: string;
  readonly wallHeight?: number;
  readonly spacing?: number;
  readonly wallCondition?: WallCondition;
  readonly source?: OpeningSource;
  readonly sourceHandle?: RoomEdgeHandle;
  readonly openingHandle: OpeningHandle | undefined;
  readonly headerAssemblyName?: string;
  readonly headerAssemblyId?: HeaderAssemblyId | null;
  readonly resolvedOpeningFraming?: ResolvedOpeningFraming;
}
export interface WallFramingBase<T extends WallFramingType> {
  readonly profileDescriptor: LumberProfileDescriptor;
  readonly rails: LumberRail[];
  readonly type: T;
  readonly meta?: WallFramingMeta;
}
export type Header = WallFramingBase<WallFramingType.HEADER> & {
  readonly id: string;
};

export type Plate = WallFramingBase<
  | WallFramingType.BOTTOM_PLATE
  | WallFramingType.TOP_PLATE
  | WallFramingType.BOTTOM_SILL_PLATE
  | WallFramingType.TOP_SILL_PLATE
>;
export type Stud = WallFramingBase<
  | WallFramingType.STANDARD_STUD
  | WallFramingType.KING_STUD
  | WallFramingType.JACK_STUD
  | WallFramingType.BOTTOM_CRIPPLE_STUD
  | WallFramingType.TOP_CRIPPLE_STUD
>;
export type Blocking = WallFramingBase<WallFramingType.BLOCKING>;
export type Framing = Plate | Stud | Header | Blocking;
export interface WallFraming {
  readonly framings: Framing[];
  readonly studClippingBSP?: NodeBSP | null;
}
export interface RoughOpening {
  readonly headerType: OpeningSource | undefined;
  readonly start: number;
  readonly end: number;
  readonly extendedStart: number;
  readonly extendedEnd: number;
  readonly bottom: number;
  readonly top: number;
  readonly sillPlateCount: number;
  readonly headerProfile: LumberProfileDescriptor;
  readonly headerAssemblyName?: string;
  readonly headerAssemblyId?: HeaderAssemblyId | null;
  readonly numberOfJackStuds: number;
  readonly numberOfKingStuds: number;
  readonly headerCount: number;
  readonly resolvedOpeningFraming: ResolvedOpeningFraming;
  readonly hasCrippleStudEnds: boolean;
  readonly hasCrippleStudsBelowOpening: boolean;
  readonly hasCrippleStudsAboveOpening: boolean;
  readonly hasTopHeaderSillPlate: boolean;
  readonly adjacentRooms: AdjacentRooms;
  readonly openingHandle: OpeningHandle | undefined;
}

export const DEFAULT_STUD_DISTANCE = 16;
export const MIN_FRAMING_STUD_LENGTH = 1.5;
